/**
 * Reports Companion activity to whatever first-party web analytics tracker
 * the *host page* happens to run, over a CustomEvent bridge.
 *
 * Why a CustomEvent and not a direct call: the widget must not hold a
 * reference to the tracker, know its name, or care whether one is present at
 * all. A page with no tracker simply has no listener and these events fall on
 * the floor, which is the correct behaviour for a third-party embed. The
 * receiving end (nuamedia-analytics `static/pa.js`) keeps an allowlist of
 * event names, so adding a name here without adding it there means the event
 * is dropped on arrival rather than appearing as an unrecognised row.
 *
 * This is deliberately NOT the same thing as `@camlin/analytics`, which
 * reports product telemetry to the runtime service. That pipeline records the
 * live session's health for us; this one records visitor behaviour for the
 * site owner. Reporting the same moment to both is fine — they answer
 * different questions for different readers — but they must not be merged.
 *
 * PRIVACY (nuamedia-analytics ADR 0008, doc 119): nothing here may carry
 * anything a visitor said, typed, or is identified by. No transcript, no
 * prompt text, no visitorId, no site key. Only counts, booleans and short
 * enumerable tokens, and the collector's own whitelist is the backstop if
 * that rule is ever broken here.
 *
 * SCOPE: one page view. Counters live at module scope rather than in React
 * state so that a widget remount — a reconnect bumps the embed's key — does
 * not read as a second visit, and so a conversation spanning a panel close
 * and reopen stays one conversation.
 */

type AvatarWebEvent =
  | 'avatar_offered'
  | 'avatar_consent'
  | 'avatar_ready'
  | 'avatar_session'
  | 'avatar_ask'
  | 'avatar_mic'
  | 'avatar_error';

type Props = Record<string, string | number | boolean>;

const BRIDGE_EVENT = 'camlin:analytics';

/**
 * The collector's own duration buckets, restated. These strings are the
 * contract: the dashboard renders them in a fixed order and an unrecognised
 * label would appear as its own silent extra bucket.
 */
function durationBucket(seconds: number): string {
  if (seconds < 5) return '<5s';
  if (seconds < 15) return '5-15s';
  if (seconds < 60) return '15-60s';
  if (seconds < 300) return '1-5m';
  return '5m+';
}

function emit(event: AvatarWebEvent, props?: Props): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(BRIDGE_EVENT, { detail: { event, props: props ?? {} } }));
  } catch {
    // Analytics must never break the widget it is watching.
  }
}

interface PageState {
  offered: boolean;
  ready: boolean;
  errored: boolean;
  micReported: Record<string, true>;
  turns: number;
  voiceUsed: boolean;
  startedAt: number;
  exitInstalled: boolean;
}

const page: PageState = {
  offered: false,
  ready: false,
  errored: false,
  micReported: {},
  turns: 0,
  voiceUsed: false,
  startedAt: 0,
  exitInstalled: false,
};

/**
 * The widget presented itself — as the consent prompt, the dormant launcher,
 * or the ordinary launcher for a visitor who already agreed. All three are
 * the offer being made, which is why this is the denominator for every other
 * avatar rate: page views would count pages the widget never appeared on, and
 * loads would count only the people who said yes.
 */
export function reportAvatarOffered(): void {
  if (page.offered) return;
  page.offered = true;
  emit('avatar_offered');
}

/** The visitor answered the offer. Only ever a fresh click, never a remembered choice. */
export function reportAvatarConsent(status: 'accepted' | 'declined'): void {
  emit('avatar_consent', { status });
}

/**
 * The 3D avatar finished loading. Deduped: a reconnect remounts the embed and
 * fires ready again, and three reconnects on one page is not three loads.
 */
export function reportAvatarReady(): void {
  if (page.ready) return;
  page.ready = true;
  emit('avatar_ready');
}

/**
 * The widget showed the visitor a fault. Deduped for the same reason as
 * ready, and more urgently: failures retry on a backoff, so counting each one
 * would turn a single broken page into a pile of errors.
 */
export function reportAvatarError(): void {
  if (page.errored) return;
  page.errored = true;
  emit('avatar_error');
}

/** A microphone permission moment. Once per outcome per page. */
export function reportAvatarMic(status: 'requested' | 'granted' | 'refused'): void {
  if (page.micReported[status]) return;
  page.micReported[status] = true;
  emit('avatar_mic', { status });
}

/**
 * An "ask the avatar about this" shortcut fired. `source` is the shortcut's
 * own id from the host page — never the prompt text, which is visitor-facing
 * copy we have no business collecting.
 */
export function reportAvatarAsk(source?: string): void {
  const token = typeof source === 'string' && /^[a-z0-9][a-z0-9-]{0,31}$/.test(source) ? source : '';
  emit('avatar_ask', token ? { source: token } : {});
}

/**
 * A visitor turn. Accumulates what the end-of-conversation summary needs,
 * because none of it exists anywhere else: the widget tracks only whether the
 * *last* turn was spoken, and keeps no conversation start time.
 */
export function noteAvatarTurn(options: { voice: boolean }): void {
  if (page.turns === 0) {
    page.startedAt = Date.now();
  }
  page.turns += 1;
  if (options.voice) {
    page.voiceUsed = true;
  }
  installExitFlush();
}

/**
 * Summarises the conversation when the visitor leaves the page.
 *
 * There is no "conversation ended" moment to hook: closing the panel
 * deliberately keeps the session warm so reopening resumes it, so treating a
 * close as an ending would split one conversation into several and inflate
 * the count. Page exit is the last instant at which the answer is known, and
 * it makes a conversation mean "a page visit where the visitor talked".
 *
 * Counters reset afterwards so a page restored from the back/forward cache
 * can report a second conversation instead of being silently ignored.
 */
export function flushAvatarSession(): void {
  if (page.turns === 0) return;
  const seconds = page.startedAt ? (Date.now() - page.startedAt) / 1000 : 0;
  emit('avatar_session', {
    turns: page.turns,
    spoke: page.voiceUsed,
    duration_bucket: durationBucket(seconds),
  });
  page.turns = 0;
  page.voiceUsed = false;
  page.startedAt = 0;
}

function installExitFlush(): void {
  if (page.exitInstalled || typeof window === 'undefined') return;
  page.exitInstalled = true;
  // pagehide, not unload: it is the event that still fires on mobile Safari
  // and the one the tracker itself flushes its page_exit on, so the two
  // arrive together and describe the same visit.
  window.addEventListener('pagehide', flushAvatarSession);
}

/** Test seam: the module's page scope is deliberately not reachable otherwise. */
export function __resetAvatarWebAnalyticsForTests(): void {
  page.offered = false;
  page.ready = false;
  page.errored = false;
  page.micReported = {};
  page.turns = 0;
  page.voiceUsed = false;
  page.startedAt = 0;
}
